import { CalendarDays, MapPin, Users, Trophy } from "lucide-react";
import { formatDate } from "./constants";

export default function MatchHeader({ match, present, settings }) {
  const isOfficial = match.match_type === "official";
  return (
    <>
      <div className="print-only mb-4">
        <div className="text-2xl font-bold">
          {settings.app_name || "ASS les Régionnaires"} — Feuille de match
        </div>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-[#0B0F17] p-6 sm:p-8">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E5B85C]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 no-print" />
        <div className="relative">
          <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-400 mb-3">
            {match.status === "finished" ? "Match terminé" : "À venir"}
          </div>
          <h1
            className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight"
            data-testid="match-title"
          >
            {isOfficial
              ? `${match.title || "Match officiel"} · vs ${match.opponent_name || "Adversaire"}`
              : match.title || "Match ASS les Régionnaires"}
          </h1>

          {match.status === "finished" && !isOfficial && (
            <div
              className="mt-5 flex items-center gap-4 flex-wrap font-display font-black text-4xl sm:text-5xl"
              data-testid="final-score"
            >
              <span className="text-sky-400">Bleus {match.score_bleus}</span>
              <span className="text-slate-500">—</span>
              <span className="text-rose-400">{match.score_rouges} Rouges</span>
            </div>
          )}

          {!isOfficial && (match.bleus_captain_id || match.rouges_captain_id) && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-mono-stat">
              {match.bleus_captain_id && (() => {
                const c = (match.players || []).find((p) => p.id === match.bleus_captain_id);
                return c ? (
                  <span
                    data-testid="captain-header-bleus"
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-sky-500/15 border border-sky-500/30 text-sky-300"
                  >
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-black bg-[#E5B85C] text-[#0B0F17]">
                      C
                    </span>
                    Bleus · {c.name}
                  </span>
                ) : null;
              })()}
              {match.rouges_captain_id && (() => {
                const c = (match.players || []).find((p) => p.id === match.rouges_captain_id);
                return c ? (
                  <span
                    data-testid="captain-header-rouges"
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300"
                  >
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-black bg-[#E5B85C] text-[#0B0F17]">
                      C
                    </span>
                    Rouges · {c.name}
                  </span>
                ) : null;
              })()}
            </div>
          )}

          {match.status === "finished" && isOfficial && (
            <div
              className="mt-5 flex items-center gap-4 flex-wrap font-display font-black text-4xl sm:text-5xl"
              data-testid="final-score-official"
            >
              <span className="text-[#E5B85C]">Nous {match.our_score ?? 0}</span>
              <span className="text-slate-500">—</span>
              <span className="text-slate-300">
                {match.opponent_score ?? 0} {match.opponent_name || "Adversaire"}
              </span>
            </div>
          )}

          {isOfficial && match.team_captain_id && (() => {
            const c = (match.players || []).find((p) => p.id === match.team_captain_id);
            return c ? (
              <div className="mt-3">
                <span
                  data-testid="captain-header-official"
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#E5B85C]/15 border border-[#E5B85C]/40 text-[#E5B85C] text-xs font-mono-stat"
                >
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-black bg-[#E5B85C] text-[#0B0F17]">
                    C
                  </span>
                  Capitaine · {c.name}
                </span>
              </div>
            ) : null;
          })()}

          {match.status === "finished" && match.man_of_the_match && (() => {
            const motm = (match.players || []).find((p) => p.id === match.man_of_the_match);
            if (!motm) return null;
            return (
              <div
                data-testid="motm-badge"
                className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#E5B85C]/15 border border-[#E5B85C]/40"
              >
                <Trophy className="w-4 h-4 text-[#E5B85C]" />
                <span className="text-[10px] font-mono-stat uppercase tracking-widest text-[#E5B85C]">
                  Homme du match
                </span>
                <span className="font-bold text-sm text-white">{motm.name}</span>
              </div>
            );
          })()}

          <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-300">
            <span className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4" /> {formatDate(match.match_date)}
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> {match.location}
            </span>
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" /> {present.length} présent(s)
            </span>
          </div>
          {match.notes && (
            <div className="mt-4 p-3 rounded-lg bg-slate-800/60 border border-white/5 text-sm text-slate-300">
              <span className="text-[10px] uppercase tracking-widest font-mono-stat text-[#E5B85C] block mb-1">
                Notes coach
              </span>
              {match.notes}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
